// Hideo - the work dates of the video dialog (src/components/fields/WorkSlotField.vue): two rows, the work start and the
// end of work, each with its own day written in full ("Tuesday 3 November") and its own time from a list of 15-minute
// slots. Logic without UI. The stored fields do not change:
// - work start: video.workStart = "YYYY-MM-DDTHH:MM" | '' (local time: a start always has a time);
// - end of work = publication: video.publishDate "YYYY-MM-DD" | '' and video.publishTime "HH:MM" | '' (no time = end
//   of that day, tlEnd).
// Start and end are chosen freely; when a change would put the end at or before the start, the other one moves by the
// same amount (the work keeps its length), so the dates never cross. The end's list starts after the start on the same
// day and shows the duration next to each slot.
import { fmtDate, t, ucFirst } from '../i18n'
import { addDays, dayOf, isoDay, mondayOf } from '../utils/date'
import { tlDur, tlParse } from './agenda'
import { tlHhmm } from './timeline'

/** The three stored fields the rows edit */
export interface WorkSlot {
  workStart: string
  publishDate: string
  publishTime: string
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^\d{2}:\d{2}$/
/** Minutes between two slots */
export const SLOT_STEP = 15
/** Time given to a start chosen by its day only */
export const DEFAULT_START = '09:00'

/** "HH:MM" → minutes from midnight (NaN if not a time) */
export const toMin = (hhmm: string): number => (TIME_RE.test(hhmm) ? +hhmm.slice(0, 2) * 60 + +hhmm.slice(3) : NaN)
/** Minutes from midnight → "HH:MM" */
export const fromMin = (m: number): string => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
/** Every slot of a day: "00:00", "00:15" … "23:45" */
export const SLOTS: readonly string[] = Array.from({ length: 24 * 60 / SLOT_STEP }, (_, i) => fromMin(i * SLOT_STEP))
/** "09:30" → "9:30" (how the rows write a time) */
export const showTime = (hhmm: string): string => (TIME_RE.test(hhmm) ? `${+hhmm.slice(0, 2)}:${hhmm.slice(3)}` : '')

/** The work start's day and time ('' each without a valid start) */
export const startDay = (v: WorkSlot): string => (tlParse(v.workStart) ? v.workStart.slice(0, 10) : '')
export const startTime = (v: WorkSlot): string => (tlParse(v.workStart) ? v.workStart.slice(11, 16) : '')
/** The end of work's day ('' without one) */
export const endDay = (v: WorkSlot): string => (DAY_RE.test(v.publishDate || '') ? v.publishDate : '')

/** Start as a date (null without one) */
const startAt = (v: WorkSlot): Date | null => tlParse(v.workStart)
/** End as a date: its time, or the end of the day without one (like tlEnd); null without a day */
const endAt = (v: WorkSlot): Date | null => {
  const d = endDay(v)
  if (!d) return null
  return TIME_RE.test(v.publishTime || '') ? new Date(`${d}T${v.publishTime}`) : new Date(`${d}T23:59`)
}
/** The end moved by ms (day and, with a time, the time; without a time only the days count) */
function moveEnd(v: WorkSlot, ms: number): WorkSlot {
  const e = endAt(v)
  if (!e) return { ...v }
  const ne = new Date(+e + ms)
  return { ...v, publishDate: isoDay(ne), publishTime: TIME_RE.test(v.publishTime || '') ? tlHhmm(ne) : '' }
}
/** The start moved by ms */
function moveStart(v: WorkSlot, ms: number): WorkSlot {
  const s = startAt(v)
  if (!s) return { ...v }
  const ns = new Date(+s + ms)
  return { ...v, workStart: `${isoDay(ns)}T${tlHhmm(ns)}` }
}
/** Start and end in order: after the start changed (old = before the change), an end at or before it moves by as much
 *  as the start did (same length of work); without an old start to measure, it becomes the end of the start's day */
function endAfterStart(v: WorkSlot, old: WorkSlot): WorkSlot {
  const s = startAt(v), e = endAt(v)
  if (!s || !e || e > s) return v
  const os = startAt(old), oe = endAt(old)
  if (os && oe && oe > os) {
    const out = moveEnd(v, +s - +os)
    const ne = endAt(out)
    if (ne && ne > s) return out
  }
  return { ...v, publishDate: startDay(v), publishTime: '' }
}
/** Start and end in order: after the end changed, a start at or after it moves back by as much as the end did; without
 *  an old end to measure, it goes on the end's day (or the day before, when even that is too late) */
function startBeforeEnd(v: WorkSlot, old: WorkSlot): WorkSlot {
  const s = startAt(v), e = endAt(v)
  if (!s || !e || e > s) return v
  const os = startAt(old), oe = endAt(old)
  if (os && oe && oe > os) {
    const out = moveStart(v, +e - +oe)
    const ns = startAt(out)
    if (ns && ns < e) return out
  }
  const same = { ...v, workStart: `${endDay(v)}T${startTime(v)}` }
  const ss = startAt(same)
  return ss && ss < e ? same : { ...v, workStart: `${isoDay(addDays(dayOf(endDay(v)), -1))}T${startTime(v)}` }
}

/** The start's day chosen: same time as before (9:00 for a new start) */
export function setStartDay(v: WorkSlot, day: string): WorkSlot {
  if (!DAY_RE.test(day)) return { ...v }
  return endAfterStart({ ...v, workStart: `${day}T${startTime(v) || DEFAULT_START}` }, v)
}
/** The start's time chosen: on the start's day, else on the end's day (the times wait for a day) */
export function setStartTime(v: WorkSlot, hhmm: string): WorkSlot {
  const day = startDay(v) || endDay(v)
  if (!day || !TIME_RE.test(hhmm)) return { ...v }
  return endAfterStart({ ...v, workStart: `${day}T${hhmm}` }, v)
}
/** The end's day chosen: the time stays */
export function setEndDay(v: WorkSlot, day: string): WorkSlot {
  if (!DAY_RE.test(day)) return { ...v }
  return startBeforeEnd({ ...v, publishDate: day }, v)
}
/** The end's time chosen ('' = no time: the end of the day). Without an end day yet, it takes the start's day, so the
 *  stored end of work always has its date. */
export function setEndTime(v: WorkSlot, hhmm: string): WorkSlot {
  const ok = TIME_RE.test(hhmm), day = endDay(v) || startDay(v)
  if (!day) return { ...v }
  return startBeforeEnd({ ...v, publishDate: day, publishTime: ok ? hhmm : '' }, v)
}
/** No start */
export const clearStart = (v: WorkSlot): WorkSlot => ({ ...v, workStart: '' })
/** No end of work (no publish day nor time) */
export const clearEnd = (v: WorkSlot): WorkSlot => ({ ...v, publishDate: '', publishTime: '' })

/** Short duration of a slot: "15 min", "1 h", "1 h 30 min"; from a day up the readable one ("2 days") */
export function slotDur(min: number): string {
  if (min >= 24 * 60) return tlDur(min * 60000)
  const h = Math.floor(min / 60), m = min % 60
  if (!h) return t('{n} min', { n: m })
  return m ? t('{h} h {m} min', { h, m }) : t('{n} h', { n: h })
}
/** The end's list: on the start's day only the slots after it; each slot with the duration from the start (none
 *  without a start) */
export function endSlots(v: WorkSlot): { time: string; dur: string }[] {
  const s = startAt(v), day = endDay(v) || startDay(v)
  if (!s || !day) return SLOTS.map(time => ({ time, dur: '' }))
  const sMin = +s / 60000
  return SLOTS
    .map(time => ({ time, min: Math.round(+new Date(`${day}T${time}`) / 60000 - sMin) }))
    .filter(x => x.min > 0)
    .map(x => ({ time: x.time, dur: slotDur(x.min) }))
}
/** The slot to show first in a list: the value, else the slot nearest to `near` ("HH:MM"), else 9:00 */
export function focusSlot(list: readonly string[], value: string, near = ''): string {
  if (list.includes(value)) return value
  const want = Number.isNaN(toMin(near)) ? 9 * 60 : toMin(near)
  return list.find(x => toMin(x) >= want) || list[list.length - 1] || ''
}

// ===== Texts =====
/** "Tuesday 3 November" / "Martedì 3 novembre" (with the year when it is not this year's) */
export function dayLabel(day: string, now = new Date()): string {
  if (!DAY_RE.test(day)) return ''
  const d = dayOf(day)
  return ucFirst(fmtDate(d, { weekday: 'long', day: 'numeric', month: 'long', ...(d.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) }))
}
// ===== Month grid (Monday first) =====
export interface GridDay { iso: string; day: number; inMonth: boolean }
/** Six weeks from the Monday of the month's first week (always 42 days: the height does not jump between months) */
export function monthGrid(month: Date): GridDay[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1), start = mondayOf(first)
  return Array.from({ length: 42 }, (_, i) => {
    const d = addDays(start, i)
    return { iso: isoDay(d), day: d.getDate(), inMonth: d.getMonth() === first.getMonth() }
  })
}
